// One PO: header (status, revision, send, PDF, Documents), then five tabs (Mitch, 2026-11-04: the What tab "has way, way
// too much information. Categorizing this information better into the tabs would make way more sense"; his pick):
// - Lines (Builds on a make PO): what's ordered. A make PO's builds at the CM (each build's parts, who supplies them,
//   what's short), then its lines' quantities and prices; the lines as a spreadsheet and the totals; a draft's Check
//   against the plan as one line over them (Review opens it beside the lines, in a dialog).
// - Terms: ship-to, payment terms, incoterms, deposit, currency, kind, their ref, notes, the run and its files; then every
//   change (History).
// - Messages: the emails back and forth (the last send, Try again, Replies, their confirmation; PoMessages), then the
//   vendor's own updates (PoUpdates).
// - Shipping: Ships by, Needed by, ordered and received, tracking, the dates on paper; then the receipts.
// - Money: its deposit and balance, the bills and the 3-way match; what's paid and still to pay; then freight, duty and
//   other costs (landed cost).
// Header fields save one at a time like any side panel; lines edit like Sell items' edit mode.
// A banner offers "Send update" when a line's spec has a newer released revision (Documents: PoDocuments).
// Packaging --- packaging lines sit in their own band with their print files; sending warns about missing ones, and a
// missing one's link opens the part's spec beside the PO to fix them (in the side pane).
// Product labels --- usePoLabels() once for the Lines and Documents tabs; sending warns about what's off on the labels.
import { ChevronDown, Files, FileText, History, ListPlus, Merge, RefreshCw, Send, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { coverDay } from "../../../../shared/cover.ts"; // Cover through ---
import type { Po, PoLine, Vendor } from "../../../../shared/db.gen.ts";
import { planToday } from "../../../../shared/planning.ts";
import {
  hasUnsentChanges,
  headerEditable,
  linesEditable,
  NEXT_STATUSES,
  PO_KIND_LABELS,
  PO_KINDS,
  PO_STATUS_LABELS,
  type PoStatus,
  poDates,
  poLinesEditable,
  poTotals,
} from "../../../../shared/po.ts";
import { type DraftCheck, needsWarning } from "../../../../shared/po-plan-check.ts";
import { shortDate, useAttachmentsByOwner } from "../../lib/costs.ts";
import { ago, dayText, money, personName, plural, qty } from "../../lib/format.ts";
import { can, mutate, mutateWithResult } from "../../lib/mutate.ts";
import { checkNow, PlanCheckRunner, usePlanCheck } from "../../lib/planning.ts"; // Check against the plan ---
import { navigate, setSearch, useSearch } from "../../lib/router.tsx";
import { me } from "../../lib/session.ts";
import { useRow, useRows } from "../../lib/sync.ts";
import { Attachments, attachPasted } from "../../ui/Attachments.tsx"; // Files ---
import { Badge, Button, cx } from "../../ui/controls.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";
import { isTyping } from "../../ui/ListPage.tsx";
import { Notice, Tabs } from "../../ui/PageParts.tsx";
import { Popover } from "../../ui/Popover.tsx";
import { type FieldDef, Fields } from "../../ui/RecordPanel.tsx";
import { PaneHead, useInPane } from "../../ui/SidePane.tsx";
import { toast } from "../../ui/toast.tsx";
import { MakeOrderView } from "../consignment/MakeOrderView.tsx"; // Consignment ---
import { type LineSpec, useLineSpecs } from "./docs.ts";
import { daysLate, poPath, STATUS_TONE, useLinesByPo, useVendorTerms, vendorLabel } from "./lib.ts";
import { PasteLinesButton, PasteLinesDialog } from "./PasteLines.tsx";
import { PoMoney } from "./PoBills.tsx"; // Bills and payments ---
import { PoDocuments } from "./PoDocuments.tsx";
import { AcknowledgeDialog, PoEmailStrip, SendToVendorButton } from "./PoEmail.tsx"; // PO email ---
import { PoLandedCost } from "./PoLandedCost.tsx"; // Landed cost ---
import { AddItemSearch, PoLines } from "./PoLines.tsx";
import { PoMessages } from "./PoMessages.tsx"; // the emails back and forth
import { PoPlanCheck, SendConfirm, sendAnyway } from "./PoPlanCheck.tsx"; // Check against the plan ---
import { PoReceiving } from "./PoReceiving.tsx";
import { PoName } from "./PoType.tsx";
import { PoUpdates, useUnseenUpdates } from "./PoUpdates.tsx"; // Order updates ---
import { type LabelGap, type PrintGap, usePoLabels, usePoPacking } from "./packaging.ts"; // Packaging ---
import { ShipTo } from "./ShipTo.tsx";
import { poLabels, ShippedDialog, TrackingText } from "./Tracking.tsx"; // Inbound tracking ---
import { VendorItemsDialog } from "./VendorItemsDialog.tsx";

type PoTab = "lines" | "terms" | "messages" | "shipping" | "money";
/** Links and habits from the tabs before (?tab=receipts from Ctrl+K, ?tab=updates from the sidebar's count). */
const OLD_TABS: Record<string, PoTab> = {
  what: "lines",
  order: "lines",
  make: "lines",
  check: "lines",
  documents: "lines",
  updates: "messages",
  email: "messages",
  when: "shipping",
  receipts: "shipping",
  history: "terms",
  invoice: "money",
  paid: "money",
  bills: "money",
  costs: "money",
};
const pickedTab = new Map<string, PoTab>();
/** A part's own heading, inside a tab that holds several (Terms, Messages, Shipping, Money). */
const PartTitle = ({ children }: { children: string }) => (
  <h3 className="border-t border-line px-4 pt-4 pb-1 text-[11px] font-medium tracking-wide text-faint uppercase first:border-t-0">{children}</h3>
);

export function PoPanel({ id, close }: { id: string; close: () => void }) {
  const inPane = useInPane(); // Side pane ---
  const po = useRow("po", id);
  const vendor = useRow("vendor", po?.vendor_id);
  const linesByPo = useLinesByPo();
  const lines = useMemo(() => linesByPo.get(id) ?? [], [linesByPo, id]);
  const terms = useVendorTerms(po?.vendor_id);
  // Side pane --- the part picked last for this PO, so back from a record opened from it (a print file's spec) lands there
  const [picked, pick] = useState<PoTab | null>(() => pickedTab.get(id) ?? null);
  const setTab = (t: PoTab) => {
    pickedTab.set(id, t);
    pick(t);
  };
  // Check against the plan --- drafts only (?tab=check opens on it). <PlanCheckRunner> works it out in idle moments, so a
  // sent PO never plans and an open draft never plans while rendering; a send works it out at once if it's behind.
  const checkable = po?.status === "draft" && po.kind !== "service";
  const { check, stale } = usePlanCheck(po, lines);
  const getCheck = () => (po && checkable ? (check && !stale ? check : checkNow(po, lines)) : null);
  const params = useSearch();
  // ?tab= is the PO's only on the POs page or its own; beside another page (Bills' Wise history, a run) it's that page's.
  const askedTab = location.pathname === "/pos" || location.pathname.startsWith("/pos/") ? params.get("tab") : null;
  const [reviewing, setReviewing] = useState(false); // Check against the plan --- beside the lines, in a dialog
  const [documents, setDocuments] = useState(false); // what the vendor gets: the link, each line's revision, downloads
  const [log, setLog] = useState(false); // the change log (every change), from More
  // ?tab=… (a receipt found with Ctrl+K, the sidebar's updates, a send warning's Review) opens its part once and leaves the
  // address, so the next PO opened starts on Lines; ?tab=check opens the check too, ?tab=documents the documents.
  useEffect(() => {
    if (!askedTab) return;
    const to = (askedTab as PoTab) in { lines: 1, terms: 1, messages: 1, shipping: 1, money: 1 } ? (askedTab as PoTab) : OLD_TABS[askedTab];
    if (to) pick(to);
    if (askedTab === "check") setReviewing(true);
    if (askedTab === "documents") setDocuments(true);
    setSearch({ tab: null });
  }, [askedTab]);
  const tab: PoTab = picked ?? "lines";
  const review = () => {
    setTab("lines");
    setReviewing(true);
  };
  const [picking, setPicking] = useState(false);
  const [pasting, setPasting] = useState(false); // Paste lines ---
  const specs = useLineSpecs(po, lines);
  const packing = usePoPacking(po, lines, specs); // Packaging ---
  const labels = usePoLabels(po, lines); // Product labels ---
  const unseen = useUnseenUpdates().get(id) ?? 0; // Order updates ---
  const shipments = useRows("po_shipment").filter((x) => x.po_id === id).length; // the vendor's shipments, on Shipping
  // Files --- a screenshot or file pasted anywhere while the PO is open goes on its files (Mitch, 2026-11-04), unless
  // it's pasted into a box being typed in, a files box of its own took it (a landed cost's), or a dialog is open over it.
  useEffect(() => {
    if (!can("attachment.update")) return;
    const onPaste = (e: ClipboardEvent) => {
      if (e.defaultPrevented || isTyping(e.target) || document.querySelector("[data-dialog]")) return;
      const files = [...(e.clipboardData?.files ?? [])];
      if (!files.length) return;
      e.preventDefault();
      void attachPasted({ type: "po", id }, files);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [id]);
  if (!po) return null;

  const headerOk = can("po.update") && headerEditable(po.status);
  const linesOk = can("po_line.update") && poLinesEditable(po); // a paper PO's too (shared/po.ts paperLines)
  const totals = poTotals(lines, po.deposit_pct);

  return (
    // The lines need ~1000px: on smaller screens the PO covers the list (Esc goes back to it). In the side pane, the pane
    // sizes it (it floats over the list when there isn't room beside it).
    <aside
      className={cx(
        "flex h-full flex-col bg-surface",
        inPane ? "w-full" : "relative z-10 w-full shrink-0 border-l border-line min-[1600px]:w-[min(1120px,calc(100%-380px))]",
      )}
    >
      <Header
        po={po}
        vendor={vendor}
        vendorShort={vendorLabel(vendor)}
        lineCount={lines.length}
        direct={lines.reduce((t, l) => t + Number(l.qty_direct ?? 0), 0)}
        close={close}
        check={check}
        getCheck={getCheck}
        review={review}
        gaps={packing.gaps}
        labelGaps={labels.gaps}
        documents={() => setDocuments(true)}
        showLog={() => setLog(true)}
      />
      {checkable && <PlanCheckRunner po={po} lines={lines} />}
      <NewerSpecs po={po} lines={lines} specs={specs} />
      <Tabs
        level="record"
        active={tab}
        onPick={setTab}
        tabs={[
          {
            key: "lines" as const,
            label: po.kind === "make" ? "Builds" : "Lines",
            badge: checkable && check && needsWarning(check, po.plan_check_key) ? <Badge tone="warn">{check.differences + check.missing}</Badge> : undefined,
          },
          // Order updates --- the vendor's updates are with the emails
          { key: "messages" as const, label: "Messages", badge: unseen > 0 ? <Badge tone="accent">{unseen} new</Badge> : undefined },
          { key: "shipping" as const, label: "Shipping" },
          { key: "money" as const, label: "Money" },
          { key: "terms" as const, label: "Terms" },
        ]}
      />
      {tab === "lines" ? (
        <div className={cx("flex min-h-0 flex-1 flex-col", po.kind === "make" && "overflow-y-auto")}>
          {/* Check against the plan --- one line over the lines; Review opens the comparison */}
          {checkable && <PlanCheckLine check={check} stale={stale} po={po} review={() => setReviewing(true)} />}
          {/* Consignment --- a make PO is its builds (units and price are edited in its builds table); a PO's lines otherwise */}
          {po.kind === "make" ? (
            <MakeOrderView po={po} lines={lines} />
          ) : (
            <div className="min-h-0 flex-1">
              {/* The lines and everything that adds to them are one unit: the table's bar */}
              <PoLines
                po={po}
                lines={lines}
                terms={terms}
                editable={linesOk}
                specs={specs}
                packing={packing.byLine}
                labels={labels.byLine}
                bar={
                  linesOk
                    ? {
                        search: <AddItemSearch po={po} lines={lines} terms={terms} />,
                        actions: (
                          <>
                            <Button size="sm" variant="ghost" icon={<ListPlus className="size-3.5" />} onClick={() => setPicking(true)}>
                              {vendorLabel(vendor)}'s items
                            </Button>
                            <PasteLinesButton onClick={() => setPasting(true)} />
                          </>
                        ),
                      }
                    : {
                        filters: (
                          <span className="text-[12px] text-muted">
                            {PO_STATUS_LABELS[po.status]}: lines can't change{po.status === "cancelled" ? " (reopen it to edit)" : ""}.
                          </span>
                        ),
                      }
                }
              />
            </div>
          )}
          <div className="flex shrink-0 items-center gap-4 border-t border-line bg-surface-2/50 px-4 py-2 text-[13px]">
            <span className="text-muted">
              {totals.lines} {totals.lines === 1 ? "line" : "lines"} · {qty(totals.qty)} units
            </span>
            {totals.unpriced > 0 && <Badge tone="warn">{totals.unpriced} without a price</Badge>}
            <span className="ml-auto text-muted">Subtotal</span>
            <span className="font-mono">{money(totals.subtotal, po.currency)}</span>
            {po.deposit_pct > 0 && (
              <>
                <span className="text-muted">Deposit {po.deposit_pct}%</span>
                <span className="font-mono">{money(totals.deposit, po.currency)}</span>
                <span className="text-muted">Balance</span>
                <span className="font-mono">{money(totals.balance, po.currency)}</span>
              </>
            )}
            <span className="font-semibold">Total {money(totals.subtotal, po.currency)}</span>
          </div>
        </div>
      ) : tab === "terms" ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="p-2">
            <Terms po={po} disabled={!headerOk} />
          </div>
          <PartTitle>Dates on the PO</PartTitle>
          <div className="grid grid-cols-2 gap-x-4 p-2">
            <Fields compact row={po} fields={dueBy} update="po.update" disabled={!headerOk} />
            {/* Paper POs --- the dates on the paper it was recorded from */}
            {(po.historical || po.dated_on || po.acknowledged_on) && (
              <div className="flex flex-col gap-0.5">
                <PaperDates po={po} />
                <OrderedReceived po={po} />
              </div>
            )}
          </div>
        </div>
      ) : tab === "messages" ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {/* PO email --- the last send (Try again, Replies, their confirmation), then every email both ways */}
          <PoEmailStrip po={po} vendor={vendor} check={check} getCheck={getCheck} review={review} gaps={packing.gaps} labelGaps={labels.gaps} />
          <PoMessages po={po} />
        </div>
      ) : tab === "shipping" ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ShippingSteps po={po} vendor={vendorLabel(vendor)} disabled={!headerOk} />
          {shipments > 0 && (
            <>
              <PartTitle>Shipments</PartTitle>
              <PoUpdates po={po} lines={lines} parts={{ lines: false, forms: [], shipments: true, timeline: false }} />
            </>
          )}
          <PartTitle>What arrived</PartTitle>
          <PoReceiving po={po} />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {/* Bills and payments --- what's billed and paid, its bills, and whether they agree with the PO and what arrived */}
          <PoMoney po={po} />
          {/* Landed cost --- the forwarder's and customs' invoices, always in view (Mitch, 2026-11-04: "keep this visible, not a
              dropdown") */}
          <PartTitle>Freight, duty and other costs</PartTitle>
          <PoLandedCost po={po} />
        </div>
      )}
      {reviewing && checkable && (
        <BigDialog title={`Check ${po.number} against the plan`} onClose={() => setReviewing(false)}>
          {check ? (
            <PoPlanCheck po={po} lines={lines} check={check} stale={stale} terms={terms} /> // Check against the plan ---
          ) : (
            <div className="p-6 text-center text-faint">Comparing with the plan…</div>
          )}
        </BigDialog>
      )}
      {log && (
        <BigDialog title={`Change log: ${po.number}`} onClose={() => setLog(false)}>
          <PoHistory id={po.id} />
        </BigDialog>
      )}
      {documents && (
        <BigDialog title={`What ${vendorLabel(vendor)} gets with ${po.number}`} onClose={() => setDocuments(false)}>
          <PoDocuments po={po} lines={lines} specs={specs} vendor={vendor} packing={packing.byLine} partPacking={packing.byPart} labels={labels.byLine} />
        </BigDialog>
      )}
      {picking && vendor && <VendorItemsDialog vendor={vendor} po={po} lines={lines} onClose={() => setPicking(false)} />}
      {pasting && vendor && <PasteLinesDialog vendor={vendor} po={po} lines={lines} onClose={() => setPasting(false)} />}
    </aside>
  );
}

function Header({
  po,
  vendor,
  vendorShort,
  lineCount,
  direct,
  close,
  check,
  getCheck,
  review,
  gaps,
  labelGaps,
  documents,
  showLog,
}: {
  po: Po;
  vendor: Vendor | undefined;
  vendorShort: string;
  lineCount: number;
  /** Pre-sales --- units on its lines for backers. */
  direct: number;
  close: () => void;
  check: DraftCheck | null;
  getCheck: () => DraftCheck | null;
  review: () => void;
  /** Packaging --- packaging parts whose revision lacks print files. */
  gaps: readonly PrintGap[];
  /** Product labels --- products whose label has something off (usePoLabels()). */
  labelGaps: readonly LabelGap[];
  /** Opens what the vendor gets (PoDocuments): the link, each line's revision, what they've downloaded. */
  documents: () => void;
  /** Opens the change log (every change to the PO, History). */
  showLog: () => void;
}) {
  const inPane = useInPane(); // Side pane --- the pane has the close button
  const users = useRows("app_user");
  const [acking, setAcking] = useState(false); // PO email --- "Acknowledged" records what the vendor confirmed
  const [shipping, setShipping] = useState(false); // Inbound tracking --- "Shipped" asks how it travels
  // Check against the plan --- the warning before Mark as sent: the plan's differences (null: none), and Packaging --- the
  // print-file gaps, and Product labels --- what's off on the labels, read live while it's open.
  const [confirming, setConfirming] = useState<{ check: DraftCheck | null } | null>(null);
  // Packaging --- with no plan differences it hides once the gaps are fixed: it's closed then, so it can't come back by itself.
  const confirmShown = !!confirming && (!!confirming.check || gaps.length > 0 || labelGaps.length > 0);
  useEffect(() => {
    if (confirming && !confirmShown) setConfirming(null);
  }, [confirming, confirmShown]);
  const gmailOn = !!me()?.gmail?.enabled;
  const who = (id: string | null) => personName(users, id);
  const unsent = hasUnsentChanges(po);
  const canSend = can("po.mark_sent") && headerEditable(po.status) && (po.status === "draft" || unsent);
  const next = NEXT_STATUSES[po.status].filter((s) => !(po.status === "draft" && s === "sent"));

  // Check against the plan --- real differences staff haven't already gone through: warn first (Review / anyway).
  const markSent = (planCheck?: { key: string; note: string }) =>
    mutateWithResult("po.mark_sent", { id: po.id, ...(planCheck ? { plan_check: planCheck } : {}) }, [
      {
        t: "po",
        id: po.id,
        row: {
          ...po,
          status: po.status === "draft" ? "sent" : po.status,
          sent_revision: po.revision,
          sent_at: new Date().toISOString(),
          ...(planCheck ? { plan_check_overridden: true, plan_check_note: planCheck.note, plan_check_key: planCheck.key } : {}),
        },
      },
    ]);
  const move = (status: PoStatus) => {
    if (status === "acknowledged" && po.status === "sent" && can("po.acknowledge")) return setAcking(true);
    if (status === "shipped") return setShipping(true);
    // Pre-sales --- cancelling a make PO hands its units for backers back to planning.
    if (
      status === "cancelled" &&
      !confirm(
        direct > 0
          ? `Cancel ${po.number}? Its ${direct.toLocaleString("en-US")} units for backers go back to planning: another open make PO on the run takes them, else they show as still to build.`
          : `Cancel ${po.number}?`,
      )
    )
      return;
    mutateWithResult("po.set_status", { id: po.id, status }, [{ t: "po", id: po.id, row: { ...po, status } }]);
  };
  const remove = async () => {
    if (!confirm(`Delete draft ${po.number}${lineCount ? ` and its ${lineCount} lines` : ""}?`)) return;
    if (await mutate("po.remove", { id: po.id })) close();
  };

  const where = statusLine(po, vendorShort, who);
  return (
    <>
      {/* Side pane --- in the pane's title bar beside its name (PaneHead); a row of its own off the pane */}
      <PaneHead fallback={(c) => <div className="flex items-center gap-3 border-b border-line px-4 py-2.5">{c}</div>}>
        <div className="min-w-0 flex-1">
          {/* Off the pane, its name here; in the pane, the pane's title line is its name */}
          {!inPane && (
            <div className="mb-0.5 text-[15px]">
              <PoName po={po} vendor={vendor} />
            </div>
          )}
          <div className="flex min-w-0 items-center gap-2 text-[13px]">
            <span className={cx("size-2 shrink-0 rounded-full", DOT[STATUS_TONE[po.status]] ?? "bg-faint")} aria-hidden="true" />
            <span className={cx("truncate", where.warn ? "text-warn" : "text-text")} title={where.text}>
              {where.text}
            </span>
            {daysLate(po) > 0 && <span className="shrink-0 text-danger">· {plural(daysLate(po), "day")} late</span>}
            {/* Shopify --- only when it didn't take (it's shown as incoming otherwise, quietly) */}
            {po.shopify_sync === "failed" && <span className="shrink-0 text-danger">· not showing as incoming in Shopify</span>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <SendToVendorButton
            po={po}
            vendor={vendor}
            primary={gmailOn && canSend}
            check={check}
            getCheck={getCheck}
            review={review}
            gaps={gaps}
            labelGaps={labelGaps}
          />
          {canSend && (
            <Button
              variant={gmailOn ? "secondary" : "primary"}
              size="sm"
              icon={<Send className="size-3.5" />}
              title={
                gmailOn
                  ? "Record that the vendor has this PO without emailing it from here"
                  : "Record that the vendor has this PO (email it from Gmail with the PDF for now)"
              }
              onClick={() => {
                const c = getCheck();
                const plan = c && needsWarning(c, po.plan_check_key) ? c : null;
                if (plan || gaps.length || labelGaps.length) setConfirming({ check: plan });
                else {
                  setConfirming(null);
                  markSent();
                }
              }}
            >
              {po.status === "draft" ? "Mark as sent" : `Mark rev ${po.revision} as sent`}
            </Button>
          )}
          <Button
            size="sm"
            icon={<FileText className="size-3.5" />}
            onClick={() => window.open(`/api/po/${po.id}/pdf?tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`, "_blank")}
          >
            PDF
          </Button>
          <Button
            size="sm"
            icon={<Files className="size-3.5" />}
            title="What the vendor gets: its documents link, each line's spec revision, what they've downloaded"
            onClick={documents}
          >
            Documents
          </Button>
          {can("po.merge") && <MergeDrafts po={po} />}
          <Popover
            width={240}
            button={(toggle) => (
              <Button size="sm" onClick={toggle} icon={<ChevronDown className="size-3.5" />}>
                More
              </Button>
            )}
          >
            {(closeMenu) => (
              <div className="flex flex-col text-[12px]">
                {can("po.set_status") && next.length > 0 && (
                  <>
                    <MenuHeading>Move to</MenuHeading>
                    {next.map((s) => (
                      <MenuItem
                        key={s}
                        onClick={() => {
                          closeMenu();
                          move(s);
                        }}
                      >
                        {PO_STATUS_LABELS[s]}
                      </MenuItem>
                    ))}
                  </>
                )}
                {/* Labels and printouts --- one per line */}
                <MenuHeading>Print labels</MenuHeading>
                {poLabels(po.id).map((it) => (
                  <a key={it.href} href={it.href} target="_blank" rel="noreferrer" onClick={closeMenu} className="rounded px-2 py-1.5 hover:bg-hover">
                    {it.label}
                  </a>
                ))}
                <MenuHeading>Records</MenuHeading>
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    showLog();
                  }}
                >
                  Change log: every change to this PO
                </MenuItem>
                {can("po.remove") && po.status === "draft" && po.sent_revision === null && (
                  <MenuItem
                    danger
                    onClick={() => {
                      closeMenu();
                      void remove();
                    }}
                  >
                    Delete this draft
                  </MenuItem>
                )}
              </div>
            )}
          </Popover>
          {!inPane && (
            <button type="button" onClick={close} className="rounded p-1 text-muted hover:bg-hover hover:text-text" title="Close (Esc)">
              <X className="size-4" />
            </button>
          )}
        </div>
      </PaneHead>
      {acking && <AcknowledgeDialog po={po} onClose={() => setAcking(false)} />}
      {shipping && <ShippedDialog po={po} onClose={() => setShipping(false)} />}
      {confirming && confirmShown && (
        <SendConfirm
          check={confirming.check}
          gaps={gaps}
          labels={labelGaps}
          sendLabel={po.status === "draft" ? "Mark as sent anyway" : `Mark rev ${po.revision} as sent anyway`}
          onClose={() => setConfirming(null)}
          onReview={() => {
            setConfirming(null);
            review();
          }}
          onSend={() => {
            setConfirming(null);
            // The plan's override is recorded only when it warned; print-file gaps and label warnings aren't recorded.
            markSent(confirming.check ? sendAnyway(confirming.check) : undefined);
          }}
        />
      )}
    </>
  );
}

/**
 * "Rev C of GCU2-MAIN was released after this PO was sent", with Send update while the lines can change. Packaging --- a
 * print-file gap that a newer revision fixes points to its button (data-send-update, packaging.ts).
 */
function NewerSpecs({ po, lines, specs }: { po: Po; lines: PoLine[]; specs: Map<string, LineSpec> }) {
  const newer = lines.flatMap((l) => {
    const s = specs.get(l.id);
    // Make POs --- and of a build's parts: "Rev B of SUB-PGC3-PCBA (in NSP3000)".
    return [
      ...(s?.newer ? [`Rev ${s.newer.rev} of ${l.sku || l.name}`] : []),
      ...(s?.parts ?? []).flatMap((p) => (p.newer ? [`Rev ${p.newer.rev} of ${p.sku || p.name} (in ${l.sku || l.name})`] : [])),
    ];
  });
  if (!newer.length) return null;
  const unique = [...new Set(newer)];
  const named = unique.length > 3 ? `${unique.slice(0, 3).join(", ")} and ${unique.length - 3} more` : unique.join(" and ");
  const canUpdate = can("po.send_spec_update") && linesEditable(po.status);
  const update = async () => {
    const { ok, result } = await mutateWithResult("po.send_spec_update", { id: po.id });
    if (ok) {
      const rev = (result as { revision: number }).revision;
      toast.ok(`Lines moved to the new revisions in rev ${rev}. Mark rev ${rev} as sent to show them on the vendor's documents page.`);
    }
  };
  return (
    <div className="flex items-center gap-3 border-b border-warn/30 bg-warn-soft px-4 py-2 text-[13px]">
      <RefreshCw className="size-4 shrink-0 text-warn" />
      <span className="min-w-0 flex-1">
        {named} {unique.length === 1 ? "was" : "were"} released after this PO was sent.{" "}
        <span className="text-muted">
          {canUpdate ? "The vendor still sees the revisions it was sent with." : "The goods have shipped, so the lines keep the revisions they were sent with."}
        </span>
      </span>
      {canUpdate && (
        <Button
          size="sm"
          variant="primary"
          icon={<Send className="size-3.5" />}
          onClick={update}
          data-send-update
          title="Moves these lines to the new revisions in the next PO revision; the vendor's documents page shows them once that revision is marked as sent"
        >
          Send update
        </Button>
      )}
    </div>
  );
}

const left: FieldDef[] = [
  { key: "payment_terms", label: "Payment terms", placeholder: "e.g. 30% deposit, 70% before shipment" },
  { key: "incoterms", label: "Incoterms", placeholder: "e.g. EXW, FOB Shenzhen" },
  { key: "deposit_pct", label: "Deposit %", type: "number", format: (v) => (v ? `${v}%` : "None") },
  { key: "currency", label: "Currency", mono: true },
  { key: "kind", label: "Kind", type: "select", options: PO_KINDS.map((k) => ({ value: k, label: PO_KIND_LABELS[k] })) },
];
const dueBy: FieldDef[] = [
  { key: "expected_ship", label: "Ships by", type: "date" },
  { key: "expected_arrive", label: "Needed by", type: "date", placeholder: "Printed as the required date" },
];
const right: FieldDef[] = [
  { key: "vendor_ref", label: "Their ref", placeholder: "PI or order confirmation number" },
  { key: "notes", label: "Notes on PO", type: "textarea", placeholder: "Printed: packaging, labelling, shipping marks" },
  { key: "internal_notes", label: "Internal notes", type: "textarea", placeholder: "Not printed" },
];

const SHIPPED = new Set<Po["status"]>(["shipped", "received", "closed"]);
const tracking: FieldDef[] = [
  { key: "carrier", label: "Carrier", placeholder: "e.g. DHL" },
  { key: "tracking_number", label: "Tracking", mono: true, placeholder: "Not printed" },
  { key: "shipped_on", label: "Shipped on", type: "date" },
];

function Terms({ po, disabled }: { po: Po; disabled: boolean }) {
  // An emptied deposit means none.
  const deposit = (value: unknown) => mutate("po.update", { id: po.id, patch: { deposit_pct: value == null ? 0 : Number(value) } });
  return (
    <div className="grid grid-cols-2 gap-x-4">
      <div className="flex min-w-0 flex-col gap-0.5">
        <ShipTo po={po} disabled={disabled} />
        <Fields
          compact
          row={po}
          // Consignment --- a make PO stays one (its kind isn't a choice)
          fields={left.filter((f) => po.kind !== "make" || f.key !== "kind").map((f) => (f.key === "deposit_pct" ? { ...f, save: deposit } : f))}
          update="po.update"
          disabled={disabled}
        />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <Fields compact row={po} fields={right} update="po.update" disabled={disabled} />
        <RunField po={po} />
        <PoFiles po={po} />
      </div>
    </div>
  );
}

/** Files --- the PO's files (the vendor's PI, quotes, screenshots of the chat): paste one anywhere on the PO, drop or choose. */
function PoFiles({ po }: { po: Po }) {
  const count = (useAttachmentsByOwner().get(`po:${po.id}`) ?? []).length;
  return (
    <div className="flex min-w-0 items-center rounded-md px-2 py-1">
      <div className="w-24 shrink-0 text-muted">Files</div>
      <Attachments owner={{ type: "po", id: po.id }} compact />
      {!count && can("attachment.update") && (
        <span className="ml-1 truncate text-[12px] text-faint">Paste a screenshot anywhere on this PO, or drop a file here</span>
      )}
    </div>
  );
}

/** Production runs --- the run this PO is on (one at most; changing it never touches what the vendor sees). */
function RunField({ po }: { po: Po }) {
  const runs = useRows("production_run");
  const projects = useRows("project");
  const label = (r: (typeof runs)[number]) => `${r.number}${r.name ? ` · ${r.name}` : ""} (${projects.find((x) => x.id === r.project_id)?.code ?? ""})`;
  const options = [
    { value: "", label: "None" },
    ...runs
      .filter((r) => r.status !== "closed" || r.id === po.production_run_id)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .map((r) => ({ value: r.id, label: label(r) })),
  ];
  const editable = can("po.link_run");
  return (
    <Fields
      compact
      row={po}
      disabled={!editable}
      fields={[
        {
          key: "production_run_id",
          label: "Production run",
          type: "select",
          nullable: true,
          options,
          save: (v) => {
            const to = (v as string) || null;
            const from = runs.find((r) => r.id === po.production_run_id);
            if (from && to && !confirm(`${po.number} is on ${from.number}. Move it?`)) return;
            mutate("po.link_run", { po_id: po.id, run_id: to, move: !!from && !!to });
          },
        },
      ]}
    />
  );
}

/** The dates on paper for a PO recorded after the fact (its own date, acknowledged), kept beside when it was recorded here. */
function PaperDates({ po }: { po: Po }) {
  const history = (key: "dated_on" | "acknowledged_on") => setHistory(po, key);
  return (
    <Fields
      compact
      row={po}
      disabled={!can("po.set_history")}
      fields={[
        { key: "dated_on", label: "Dated (on paper)", type: "date", placeholder: "If it was made before HHL Ops", save: history("dated_on") },
        { key: "acknowledged_on", label: "Acknowledged (on paper)", type: "date", save: history("acknowledged_on") },
      ]}
    />
  );
}

/**
 * Check against the plan --- one line over a draft's lines: whether the plan still says the same, and Review, which opens
 * the comparison (PoPlanCheck) beside the lines.
 */
function PlanCheckLine({ po, check, stale, review }: { po: Po; check: DraftCheck | null; stale: boolean; review: () => void }) {
  const differs = !!check && needsWarning(check, po.plan_check_key);
  const n = check ? check.differences + check.missing : 0;
  return (
    <Notice
      tone={differs ? "warn" : "info"}
      actions={
        <Button size="sm" variant={differs ? "primary" : "ghost"} onClick={review}>
          {differs ? "Review" : "Open"}
        </Button>
      }
    >
      {!check || stale
        ? "Checking it against the plan…"
        : differs
          ? `The plan says something else now for ${plural(n, "item")}: check before sending.`
          : "Checked against the plan: nothing new since it was drafted."}
    </Notice>
  );
}

/** A large dialog for a part that's a page of its own (Check against the plan, Documents), opened beside the PO. */
function BigDialog({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="flex h-[80vh] w-[1000px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-pop"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-line px-4 py-3">
          <div className="min-w-0 flex-1 truncate text-[15px] font-semibold">{title}</div>
          <button type="button" onClick={onClose} className="rounded p-1 text-muted hover:bg-hover hover:text-text" title="Close (Esc)">
            <X className="size-4" />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      </div>
    </DialogLayer>
  );
}

const setHistory = (po: Po, key: "dated_on" | "sent_on" | "acknowledged_on" | "received_on") => (v: unknown) =>
  mutate("po.set_history", { id: po.id, [key]: (v as string) || null });

/**
 * How long it took (migration 078): ordered → received, for lead times. Beside Needed by, in sight without scrolling
 * (Mitch, 2026-11-04: "I do not see anywhere to edit those"), at any status: a received or closed PO is when it's typed.
 */
function OrderedReceived({ po }: { po: Po }) {
  return (
    <>
      <Fields
        compact
        row={po}
        disabled={!can("po.set_history")}
        fields={[
          { key: "sent_on", label: "Ordered on", type: "date", placeholder: "The day it went, if not sent from here", save: setHistory(po, "sent_on") },
          {
            key: "received_on",
            label: "Received on",
            type: "date",
            placeholder: "The day it all arrived, if not received here",
            save: setHistory(po, "received_on"),
          },
        ]}
      />
      <TookDays po={po} />
    </>
  );
}

/** How long it took, ordered → received (poDates()), and making that its vendor's lead time for these items. */
function TookDays({ po }: { po: Po }) {
  const receipts = useRows("receipt").filter((r) => r.po_id === po.id);
  const vendor = useRow("vendor", po.vendor_id);
  const lines = useRows("po_line").filter((l) => l.po_id === po.id);
  const links = useRows("vendor_item").filter((l) => l.vendor_id === po.vendor_id && lines.some((x) => x.item_id === l.item_id));
  const d = poDates(po, receipts);
  if (d.days == null) return null;
  const same = lines.length > 0 && lines.every((l) => links.find((k) => k.item_id === l.item_id)?.lead_time_days === d.days);
  const name = vendor ? vendorLabel(vendor) : "the vendor";
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 pb-1 text-[12px]">
      <span>
        Took <span className="font-medium">{plural(d.days, "day")}</span>
        <span className="text-muted">
          {" "}
          (ordered {shortDate(d.ordered)}, received {shortDate(d.received)})
        </span>
      </span>
      {same ? (
        <span className="text-faint">{name}'s lead time for these</span>
      ) : (
        can("po.use_lead_time") && (
          <Button
            size="sm"
            variant="ghost"
            title={`Set ${name}'s lead time for ${plural(lines.length, "item")} on this PO to ${d.days} days, so planning orders them that far ahead`}
            onClick={async () => {
              const { ok, result } = await mutateWithResult("po.use_lead_time", { po_id: po.id, days: d.days as number });
              const r = (result ?? {}) as { updated?: number; made?: number };
              if (ok) toast.ok(`${name}'s lead time is ${d.days} days for ${plural((r.updated ?? 0) + (r.made ?? 0), "item")}`);
            }}
          >
            Use as {name}'s lead time
          </Button>
        )
      )}
    </div>
  );
}

type Entry = {
  id: number;
  at: string;
  via: string;
  action: string;
  entity: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  user_name: string | null;
  user_email: string | null;
  line_sku: string | null;
};

const HIDDEN = new Set([
  "id",
  "po_id",
  "item_id",
  "created_at",
  "updated_at",
  "photo_url",
  "position",
  "qty_shipped",
  "qty_received",
  "qty_rejected",
  "plan_basis", // Check against the plan --- the snapshot and the fingerprint aren't for reading
  "plan_check_key",
]);
// Shopify bookkeeping (the Receipts tab shows it) and the Gmail thread ids (the email strip); receipts do show what they received.
const SHOPIFY_KEYS = /^(shopify|gmail)_/;
const RECEIVED = new Set(["qty_received", "qty_rejected"]);
const ISO = /^\d{4}-\d{2}-\d{2}T[\d:.]+Z$/;
/** A changed value as people read it: dates without midnight times, user ids as names, spec revisions as "Rev C". */
const shown = (key: string, v: unknown, userName: (id: string) => string, revName: (id: string) => string) => {
  if (v == null || v === "") return "empty";
  if (typeof v === "string" && key === "spec_revision_id") return revName(v);
  if (typeof v === "string" && key.endsWith("_by")) return userName(v);
  if (typeof v === "string" && ISO.test(v)) return v.includes("T00:00:00") ? v.slice(0, 10) : new Date(v).toLocaleString();
  return typeof v === "object" ? JSON.stringify(v) : String(v);
};

function describe(e: Entry) {
  const line = e.line_sku || "a line";
  if (e.entity === "doc_link")
    return !e.before ? "made the documents link" : e.after?.revoked_at ? "turned off the documents link" : "changed the documents link";
  if (e.action === "po.send_spec_update" && e.entity === "po_line") return `sent the spec update for ${line}`;
  // Check against the plan ---
  if (e.action === "po.update_from_plan" && e.entity === "po_line")
    return !e.before ? `added ${line} from the plan` : !e.after ? `took ${line} off (the plan doesn't need it now)` : `set ${line} to the plan's quantity`;
  if (e.action === "po.update_from_plan" && e.entity === "po" && e.after?.plan_checked_at) return "updated it from the plan";
  if (e.action === "po.plan_checked") return "checked it against the plan and kept it as it is";
  // Its starting point, recorded as it was made (server/src/po-plan-check.ts recordDraftStart()).
  if ((e.action === "po.create" || e.action === "po.create_make") && e.entity === "po" && e.before && e.after?.plan_checked_at)
    return "checked it against the plan as it was drafted";
  if (e.after?.plan_check_overridden) return "sent it although it differed from the plan";
  if (e.entity === "po_line") return !e.before ? `added ${line}` : !e.after ? `removed ${line}` : `changed ${line}`;
  if (e.action === "receipt.post") return e.entity === "po_line" ? `received ${line}` : "received a delivery";
  if (!e.before) return "created this PO";
  if (e.action === "po.send_email") return e.after?.status === "sent" || e.after?.sent_revision !== undefined ? "emailed it to the vendor" : "changed"; // PO email ---
  if (e.action === "po.acknowledge") return "recorded the vendor's confirmation";
  if (e.action === "po.mark_sent" || (e.action === "po.set_status" && e.after?.status === "sent")) return "marked it as sent";
  return "changed";
}

/** The PO's changes and its lines', newest first. */
function PoHistory({ id }: { id: string }) {
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const users = useRows("app_user");
  const userName = (uid: string) => personName(users, uid);
  const revisions = useRows("spec_revision");
  const revName = (rid: string) => `Rev ${revisions.find((r) => r.id === rid)?.rev ?? "?"}`;
  useEffect(() => {
    let live = true;
    fetch(`/api/po/${id}/history`)
      .then((r) => r.json())
      .then((rows: Entry[]) => live && setEntries(rows));
    return () => {
      live = false;
    };
  }, [id]);
  if (!entries) return null;
  return (
    <ol className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
      {entries.map((e) => {
        const keys = Object.keys(e.after ?? {}).filter((k) => (!HIDDEN.has(k) || (e.action === "receipt.post" && RECEIVED.has(k))) && !SHOPIFY_KEYS.test(k));
        if (e.before && e.after && !keys.length) return null;
        return (
          <li key={e.id} className="flex gap-2">
            <History className="mt-0.5 size-3.5 shrink-0 text-faint" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="font-medium">{e.user_name || e.user_email || "HHL Ops"}</span>
                <span className="text-muted">{describe(e)}</span>
                {e.via !== "ui" && <Badge>{e.via}</Badge>}
                <span className="ml-auto text-[11px] text-faint">{new Date(e.at).toLocaleString()}</span>
              </div>
              {e.before && e.after && (
                <div className="mt-1 flex flex-col gap-0.5 text-[12px]">
                  {keys.map((k) => (
                    <div key={k} className="truncate">
                      <span className="text-muted">{k}: </span>
                      <span className="text-faint line-through">{shown(k, e.before?.[k], userName, revName)}</span> →{" "}
                      <span>{shown(k, e.after?.[k], userName, revName)}</span>
                    </div>
                  ))}
                </div>
              )}
              {!e.before && e.entity === "po_line" && e.after && (
                <div className="mt-1 text-[12px] text-muted">
                  {qty(e.after.qty)} × {e.after.unit_price == null ? "no price" : money(e.after.unit_price)}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Merge drafts (Mitch, 2026-11-03): this draft's lines onto another draft to the same vendor and ship-to (an item on
 * both is one line with both quantities), and this one goes (po.merge). Shown only when there's another to merge into.
 */
function MergeDrafts({ po }: { po: Po }) {
  const pos = useRows("po");
  const lines = useRows("po_line");
  const mergeable = (p: Po) => p.status === "draft" && p.kind === "purchase" && p.sent_revision === null;
  if (!mergeable(po)) return null;
  const others = pos
    .filter((p) => p.id !== po.id && p.vendor_id === po.vendor_id && mergeable(p) && (p.ship_to_location_id ?? null) === (po.ship_to_location_id ?? null))
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  if (!others.length) return null;
  const count = (id: string) => lines.filter((l) => l.po_id === id).length;
  const merge = async (into: Po) => {
    const { ok } = await mutateWithResult("po.merge", { from_id: po.id, into_id: into.id });
    if (!ok) return;
    toast.ok(`${po.number} merged into ${into.number}`);
    location.pathname === "/pos" ? setSearch({ open: into.id, pane: null }) : navigate(poPath(into.id));
  };
  return (
    <Popover
      width={320}
      button={(toggle) => (
        <Button
          size="sm"
          variant="ghost"
          icon={<Merge className="size-3.5" />}
          onClick={toggle}
          title="Put this draft's lines on another draft to the same vendor"
        >
          Merge…
        </Button>
      )}
    >
      {(close) => (
        <div className="flex flex-col gap-1">
          <div className="pb-1 font-medium">Merge {po.number} into…</div>
          <div className="pb-1 text-[12px] text-muted">
            Its {count(po.id)} line{count(po.id) === 1 ? "" : "s"} go onto the draft you pick (an item on both becomes one line with both quantities), and{" "}
            {po.number} is deleted.
          </div>
          {others.map((o) => (
            <button
              key={o.id}
              type="button"
              className="flex items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-hover"
              onClick={() => {
                close();
                void merge(o);
              }}
            >
              <span className="font-mono text-[12px] font-medium">{o.number}</span>
              <span className="text-[12px] text-muted">
                {count(o.id)} line{count(o.id) === 1 ? "" : "s"} · made {ago(o.created_at)}
              </span>
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

const DOT: Record<string, string> = { accent: "bg-accent", ok: "bg-ok", warn: "bg-warn", danger: "bg-danger", neutral: "bg-faint" };
const MenuHeading = ({ children }: { children: string }) => (
  <div className="px-2 pt-2 pb-0.5 text-[10px] font-semibold tracking-wide text-faint uppercase first:pt-0">{children}</div>
);
const MenuItem = ({ children, onClick, danger }: { children: string; onClick: () => void; danger?: boolean }) => (
  <button type="button" onClick={onClick} className={cx("rounded px-2 py-1.5 text-left hover:bg-hover", danger && "text-danger")}>
    {children}
  </button>
);

/**
 * Where a PO stands, as one sentence (Mitch, 2026-11-04: little status tags "don't mean anything" to a regular person):
 * "Sent 1 hour ago by Mitch · waiting for Elecrow to confirm", "Elecrow confirmed · ships Dec 1".
 */
function statusLine(po: Po, vendor: string, who: (id: string | null) => string): { text: string; warn?: boolean } {
  const ships = po.vendor_ship_date ?? po.expected_ship;
  const shipsText = ships ? ` · ships ${dayText(ships)}` : "";
  if (po.status === "draft")
    return {
      text: `Draft, not sent yet · made ${ago(po.created_at)}${po.created_by ? ` by ${who(po.created_by)}` : ""}${po.cover_through ? ` · covers to ${coverDay(po.cover_through, planToday())}` : ""}`,
    };
  if (hasUnsentChanges(po)) return { text: `Changed since ${vendor} got it: send rev ${po.revision} so they have the latest`, warn: true };
  if (po.status === "sent") return { text: `Sent ${ago(po.sent_at)}${po.sent_by ? ` by ${who(po.sent_by)}` : ""} · waiting for ${vendor} to confirm` };
  if (po.status === "acknowledged") return { text: `${vendor} confirmed${po.vendor_ref ? ` (their order ${po.vendor_ref})` : ""}${shipsText}` };
  if (po.status === "in_production") return { text: `${vendor} is making it${shipsText}` };
  if (po.status === "shipped")
    return {
      text: `Shipped${po.shipped_on ? ` ${dayText(po.shipped_on)}` : ""}${po.tracking_number ? ` · ${po.carrier ? `${po.carrier} ` : ""}${po.tracking_number}` : ""} · on its way`,
    };
  if (po.status === "received") return { text: `Received${po.received_on ? ` ${dayText(po.received_on)}` : ""} · everything is in` };
  if (po.status === "closed") return { text: "Closed: nothing else is coming" };
  return { text: "Cancelled" };
}

/**
 * How it travels, step by step (Mitch, 2026-11-04: "Who fills these out? why? When would this even apply?"): ordered (when
 * it went), confirmed (by the vendor: in the portal, or Move to → Acknowledged), shipped (the carrier and tracking: the
 * vendor's shipment, Move to → Shipped, or typed here) and received (the receiving scan fills it in).
 */
function ShippingSteps({ po, vendor, disabled }: { po: Po; vendor: string; disabled: boolean }) {
  const shipped = SHIPPED.has(po.status) || !!po.shipped_on || !!po.tracking_number;
  const received = po.status === "received" || po.status === "closed";
  // What a step waits on is a button in the step (Mitch, 2026-11-04: "Hiding options in sub-menus is pretty horrible for
  // most items that require action"); More → Move to has them too.
  const [acking, setAcking] = useState(false);
  const [shipping, setShipping] = useState(false);
  const canAck = po.status === "sent" && can("po.acknowledge");
  const canShip = !shipped && can("po.set_status") && NEXT_STATUSES[po.status].includes("shipped");
  const steps = [
    {
      title: "Ordered",
      done: po.sent_revision != null,
      text: po.sent_revision != null ? `Sent ${dayText(po.sent_on ?? po.sent_at)}` : "Not sent yet",
      action: null,
    },
    {
      title: `Confirmed by ${vendor}`,
      done: !!po.acknowledged_at || po.status !== "sent",
      text: po.acknowledged_at
        ? `${dayText(po.acknowledged_at)}${po.vendor_ship_date ? ` · they say it ships ${dayText(po.vendor_ship_date)}` : ""}`
        : po.sent_revision != null
          ? `Waiting for ${vendor} to confirm in the vendor portal, or record what they told you`
          : "",
      action: canAck ? { label: "Record their confirmation", run: () => setAcking(true) } : null,
    },
    {
      title: "Shipped",
      done: shipped,
      text: shipped
        ? `${po.shipped_on ? dayText(po.shipped_on) : "On its way"}${po.carrier || po.tracking_number ? ` · ${[po.carrier, po.tracking_number].filter(Boolean).join(" ")}` : ""}`
        : `Not yet: ${vendor} adds the shipment and tracking in the vendor portal, or record it yourself`,
      action: canShip ? { label: "Mark as shipped", run: () => setShipping(true) } : null,
    },
    {
      title: "Received",
      done: received,
      text: received ? `${po.received_on ? dayText(po.received_on) : "Everything is in"}` : "Receiving fills this in when it's scanned in",
      action: null,
    },
  ] as { title: string; done: boolean; text: string; action: { label: string; run: () => void } | null }[];
  return (
    <div className="p-4">
      <ol className="flex flex-col">
        {steps.map((st, i) => (
          <li key={st.title} className="flex gap-3 pb-3 last:pb-0">
            <span
              className={cx(
                "flex size-6 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold",
                st.done ? "bg-ok text-white" : "bg-surface-2 text-muted",
              )}
            >
              {st.done ? "✓" : i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className={cx("font-medium", !st.done && "text-muted")}>{st.title}</div>
              {(st.text || st.action) && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted">
                  {st.text && <span>{st.text}</span>}
                  {st.action && (
                    <Button size="sm" onClick={st.action.run}>
                      {st.action.label}
                    </Button>
                  )}
                </div>
              )}
              {/* Inbound tracking --- typed here when it's known some other way; HHL's own, never printed */}
              {st.title === "Shipped" && shipped && (
                <div className="mt-1 max-w-md">
                  <Fields compact row={po} fields={tracking} update="po.update" disabled={disabled} />
                  <TrackingText po={po} className="px-2 pb-1 text-[12px]" />
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
      {acking && <AcknowledgeDialog po={po} onClose={() => setAcking(false)} />}
      {shipping && <ShippedDialog po={po} onClose={() => setShipping(false)} />}
    </div>
  );
}
